import { useState, useCallback, memo, useRef, forwardRef } from 'react';
import { useTracking } from 'react-tracking';
import {
  FastCommentsWidget,
  useFastCommentsCount,
} from '@/components/FastCommentWidget';
import { Sentry } from '@/components/Sentry';
import useUser from '@/hooks/useUser';
import loadable from '@loadable/component';
import If from '@/components/If';
import { usePersistentStorage } from '@/hooks/usePersistentStorage';
import { useFlag } from '@/features/unleash';

import GuidelinesNotice from './GuidelinesNotice';
import CommentCount from './CommentCount';
import { COMMUNITY_GUIDELINES_ACCEPTED } from './constants';
import { cn } from '@simplywallst/ui-core/next';

const GuestRegistrationModal = loadable(
  () => import('@components/GuestRegistrationModal'),
  {
    ssr: false,
  }
);

const CommunityGuidelinesModal = loadable(
  () => import('./CommunityGuidelines'),
  {
    ssr: false,
  }
);

const FastComments = memo(FastCommentsWidget);

interface Props {
  commentKey: string;
  className?: string;
}

export const NarrativeComment = forwardRef<HTMLDivElement, Props>(
  ({ commentKey, className }: Props, ref) => {
    const { trackEvent } = useTracking();
    const { enabled, flagsReady } = useFlag({ name: 'mono-narrative-comment' });
    const { setCommentCount } = useFastCommentsCount(commentKey);
    const { set, get } = usePersistentStorage();
    const continueSubmitFnRef = useRef<() => void>();
    const cancelFnRef = useRef<() => void>();
    const [state, setState] = useState<
      'idle' | 'registration' | 'community_guidelines'
    >('idle');

    const confirmationCallback = useCallback(
      (status) => {
        setState('idle');
        if (status) {
          set(COMMUNITY_GUIDELINES_ACCEPTED, 'true');
          if (typeof continueSubmitFnRef.current === 'function') {
            trackEvent({
              modifier: 'createComment',
              action: 'click',
            });
            continueSubmitFnRef.current();
          }
          return;
        }
        if (typeof cancelFnRef.current === 'function') {
          cancelFnRef.current();
        }
      },
      [set]
    );

    const onCommentSubmitStart = useCallback(
      async (_, continueSubmitFn, cancelFn) => {
        const isAccepted = await get(COMMUNITY_GUIDELINES_ACCEPTED);
        if (isAccepted === 'true') {
          trackEvent({
            modifier: 'createComment',
            action: 'click',
          });
          continueSubmitFn();
          return;
        }
        setState('community_guidelines');
        continueSubmitFnRef.current = continueSubmitFn;
        cancelFnRef.current = cancelFn;
      },
      [get]
    );

    const openRegistration = useCallback(() => {
      setState('registration');
    }, []);

    const { data: userData } = useUser();
    if (!enabled || !flagsReady) {
      return null;
    }
    return (
      <Sentry
        sentryCapture={{ team: 'narwhals', extra: { commentKey } }}
        globalDisabled
      >
        <If match={state === 'community_guidelines'}>
          {() => (
            <CommunityGuidelinesModal
              onClose={() => confirmationCallback(false)}
              onDecline={() => confirmationCallback(false)}
              onAccept={() => confirmationCallback(true)}
            />
          )}
        </If>
        <If match={state === 'registration'}>
          {() => (
            <GuestRegistrationModal
              canClose={true}
              onClose={() => {
                setState('idle');
              }}
              onRegisterSuccess={() => {
                setState('idle');
              }}
              onLoginSuccess={() => {
                setState('idle');
              }}
            />
          )}
        </If>
        <div
          className={cn(
            'rounded-none bg-surface-1 px-x3 py-x2 md:rounded-x1',
            className
          )}
          ref={ref}
        >
          <div className="grid grid-cols-[max-content_max-content] gap-x0_5">
            <h2 className="m-0 text-sm font-normal text-softer">Comments</h2>
            <div>
              <CommentCount commentKey={commentKey} />{' '}
              <span className="sr-only">comments</span>
            </div>
          </div>
          <GuidelinesNotice
            openCommunityGuidelines={() => setState('community_guidelines')}
          />
          <FastComments
            setShowRegModal={openRegistration}
            onCommentSubmitStart={onCommentSubmitStart}
            commentCountUpdated={setCommentCount}
            urlId={commentKey}
            userData={userData}
          />
        </div>
      </Sentry>
    );
  }
);

NarrativeComment.displayName = 'NarrativeComment';
